import { FC, useState, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { ConversationLanguage } from "../../../models/conversation/Message";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";

const LANGUAGE_CONFIG = {
  da: { display: "Danish", shortCode: "DA" },
  en: { display: "English", shortCode: "EN" },
  sw: { display: "Swedish", shortCode: "SE" },
} as const;

type Props = {
  selectedLanguage: ConversationLanguage | null;
  onChange: (lang: ConversationLanguage) => void;
  disabled: boolean;
};

const ConversationLanguageButton: FC<Props> = ({
  selectedLanguage,
  onChange,
  disabled,
}) => {
  const [language, setLanguage] = useState<ConversationLanguage | null>(
    selectedLanguage
  );
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const handleLanguageChange = (code: string, display: string) => {
    const newLanguage = { code, name: display };
    setLanguage(newLanguage);
    onChange(newLanguage);
  };

  const handleDropdownClose = () => {
    setTimeout(() => setTooltipVisible(false), 0);
  };

  const languageIcon = useMemo(
    () => (
      <div
        className={cn(
          "w-8 h-5 text-xs leading-5 select-none",
          language?.code ? "w-5" : "w-8"
        )}
      >
        {language?.code
          ? LANGUAGE_CONFIG[language.code as keyof typeof LANGUAGE_CONFIG]
              ?.shortCode
          : "Lang"}
      </div>
    ),
    [language?.code]
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger
          asChild
          onPointerEnter={disabled ? undefined : () => setTooltipVisible(true)}
          onPointerLeave={disabled ? undefined : () => setTooltipVisible(false)}
        >
          <DropdownMenuTrigger
            className="px-2.5 rounded-sm hover:bg-accent hover:text-accent-foreground"
            asChild
          >
            <Button
              className="select-none px-2.5 py-5"
              variant="ghost"
              onClick={() => {
                if (!disabled) {
                  setIsOpen(!isOpen);
                }
              }}
              disabled={disabled}
            >
              {languageIcon}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        {tooltipVisible && (
          <TooltipContent>
            <p>Vælg sprog</p>
          </TooltipContent>
        )}
      </Tooltip>
      <DropdownMenuContent onCloseAutoFocus={handleDropdownClose}>
        {Object.entries(LANGUAGE_CONFIG).map(([code, { display }]) => (
          <DropdownMenuItem
            key={code}
            className={cn(
              "cursor-pointer",
              language?.code === code && "bg-primary text-primary-foreground"
            )}
            onClick={() => handleLanguageChange(code, display)}
          >
            {display}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ConversationLanguageButton;
